import { apiClient } from "../../../services/axiosInstance";
import {
  Bundle,
  BundlePurchasePayload,
  Release,
  ServerListResponse,
  ServerSingleResponse,
} from "../../../types";
import {
  OwnerPaymentServiceTokenTypes,
  SubscriptionOffersTypes,
  SubscriptionProviderMetaType,
  SubscriptionOffersV2Type,
} from "../types";
import { persistStripeReturnSlideInMenuState } from "../utils/stripeReturnSlideInMenu";
import { isRecord } from "../utils/replaceCompletedEntityInCache";

export const GET_SUBSCRIPTION_PRICING_QUERY_KEY = ["get-subscription-pricing"];
export function getSubscriptionPricing(promoCode?: string) {
  return apiClient.get<ServerSingleResponse<SubscriptionOffersTypes>>(
    `/v1/account/webnext-subscription-pricing?code=${promoCode ?? ""}`,
  );
}

export const GET_SUBSCRIPTION_PRICING_V2_QUERY_KEY = [
  "get-subscription-pricing-v2",
];
export function getSubscriptionPricingV2(
  promoCode?: string,
  userOfferUuid?: string,
) {
  return apiClient.post<ServerSingleResponse<SubscriptionOffersV2Type>>(
    `/v1/account/subscription-pricing-v2`,
    {
      meta: {},
      payload: {
        promo_code: promoCode || undefined,
        user_offer_uuid: userOfferUuid || undefined,
      },
    },
  );
}

export const GET_GIFTCARD_PRICING_QUERY_KEY = ["get-giftcard-pricing"];
export function getGiftCardPricing(promoCode?: string) {
  return apiClient.get<ServerSingleResponse<SubscriptionOffersTypes>>(
    `/v1/account/webnext-giftcard-pricing?code=${promoCode ?? ""}`,
  );
}

export type WebPurchasePayloadTypes = {
  accessTokenTypeId: number;
  totalMinorUnits: number;
  stripeToken?: string;
  ownerPaymentServiceTokenId?: string;
  incentiveCode?: string | null;
};
export function webPurchase(payload: WebPurchasePayloadTypes) {
  return apiClient.post<ServerSingleResponse<any>>(`/v1/account/web-purchase`, {
    meta: {},
    payload,
  });
}

export type WebSubscriptionCheckoutSessionPayload = {
  accessTokenTypeId: number;
  incentiveCode?: string | null;
  userOfferUuid?: string;
  context?: string;
  welcomeGiftId?: number;
};

export type WebSubscriptionSetupSessionPayload =
  WebSubscriptionCheckoutSessionPayload & {
    checkoutFlow: "subscription_setup";
  };

export type WebGiftCardCheckoutSessionPayload = {
  accessTokenTypeId: number;
  incentiveCode?: string | null;
};

export type GiftCardPaymentSetupPayload = WebGiftCardCheckoutSessionPayload & {
  checkoutFlow: "gift_card_payment_setup";
  confirmationTokenId: string;
};

export type WebSubscriptionCheckoutSessionResponse = {
  entity_type: "WebSubscriptionCheckoutSession";
  provider: string;
  session_id: string;
  checkout_url: string;
  expires_at: string;
  /**
   * Server-issued nonce returned for guest setup sessions only. Persist this
   * alongside the pending setup payload and pass back to the finalize
   * endpoint — it's how the server proves the finalize call came from the
   * same browser that started the flow.
   */
  client_nonce?: string;
};

export type WebSubscriptionSetupIntentResponse = {
  entity_type: "WebSubscriptionSetupIntent";
  provider: string;
  setup_intent_id: string;
  client_secret: string;
  client_nonce: string;
};

export function getCheckoutReturnPath() {
  persistStripeReturnSlideInMenuState();
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function createWebSubscriptionSetupIntent(
  payload: WebSubscriptionCheckoutSessionPayload,
) {
  return apiClient.post<ServerSingleResponse<WebSubscriptionSetupIntentResponse>>(
    `/v1/account/web-subscription-setup-intent`,
    {
      meta: {},
      payload: {
        ...payload,
        welcome_gift_id: payload.welcomeGiftId,
      },
    },
  );
}

export type WebSubscriptionCreateFromSetupSessionPayload =
  WebSubscriptionCheckoutSessionPayload & {
    setupIntentId: string;
    clientNonce?: string;
  };

export type WebSubscriptionCreateFromSetupSessionResponse = {
  entity_type: "WebSubscriptionSetupCompletion";
  provider: string;
  subscription_id: string;
  customer_id: string;
};

export function createWebSubscriptionFromSetupSession(
  payload: WebSubscriptionCreateFromSetupSessionPayload,
) {
  return apiClient.post<
    ServerSingleResponse<WebSubscriptionCreateFromSetupSessionResponse>
  >(`/v1/account/web-subscription-from-setup-session`, {
    meta: {},
    payload: {
      accessTokenTypeId: payload.accessTokenTypeId,
      incentiveCode: payload.incentiveCode,
      userOfferUuid: payload.userOfferUuid,
      context: payload.context,
      welcome_gift_id: payload.welcomeGiftId,
      setup_intent_id: payload.setupIntentId,
      client_nonce: payload.clientNonce,
    },
  });
}

export function createWebSubscriptionFromSavedPaymentMethod(
  payload: WebSubscriptionCheckoutSessionPayload,
) {
  return apiClient.post<
    ServerSingleResponse<WebSubscriptionCreateFromSetupSessionResponse>
  >(`/v1/account/web-subscription-from-saved-payment-method`, {
    meta: {},
    payload: {
      accessTokenTypeId: payload.accessTokenTypeId,
      incentiveCode: payload.incentiveCode,
      userOfferUuid: payload.userOfferUuid,
      context: payload.context,
      welcome_gift_id: payload.welcomeGiftId,
    },
  });
}

export function createGiftCardPurchaseFromConfirmationToken(
  payload: GiftCardPaymentSetupPayload,
) {
  return apiClient.post<ServerSingleResponse<{ status: "success"; order_id: number | null }>>(
    `/v1/account/giftcard-purchase-from-confirmation-token`,
    {
      meta: {},
      payload: {
        accessTokenTypeId: payload.accessTokenTypeId,
        incentiveCode: payload.incentiveCode,
        confirmation_token_id: payload.confirmationTokenId,
      },
    },
  );
}

export function createGiftCardPurchaseFromSavedPaymentMethod(
  payload: WebGiftCardCheckoutSessionPayload,
) {
  return apiClient.post<ServerSingleResponse<{ status: "success"; order_id: number | null }>>(
    `/v1/account/giftcard-purchase-from-saved-payment-method`,
    {
      meta: {},
      payload: {
        accessTokenTypeId: payload.accessTokenTypeId,
        incentiveCode: payload.incentiveCode,
      },
    },
  );
}

export type ReleaseCheckoutSessionPayload = Pick<
  ReleasePurchasePayload,
  "price_point_id" | "full_price" | "is_gold" | "is_sale"
>;

export type ReleasePaymentSetupPayload = ReleaseCheckoutSessionPayload & {
  checkoutFlow: "release_payment_setup";
  releaseId: number;
  confirmationTokenId: string;
};

export type BundlePaymentSetupPayload = BundlePurchasePayload & {
  checkoutFlow: "bundle_payment_setup";
  bundleUuid: string;
  confirmationTokenId: string;
};

export function createReleasePurchaseFromConfirmationToken(
  releaseId: number,
  payload: ReleasePaymentSetupPayload,
) {
  return apiClient.post<ServerSingleResponse<Release>>(
    `/v1/releases/${releaseId}/purchase-from-confirmation-token`,
    {
      meta: {},
      payload: {
        price_point_id: payload.price_point_id,
        full_price: payload.full_price,
        is_gold: payload.is_gold,
        is_sale: payload.is_sale,
        confirmation_token_id: payload.confirmationTokenId,
      },
    },
  );
}

export function createReleasePurchaseFromSavedPaymentMethod(
  releaseId: number,
  payload: ReleaseCheckoutSessionPayload,
) {
  return apiClient.post<ServerSingleResponse<Release>>(
    `/v1/releases/${releaseId}/purchase-from-saved-payment-method`,
    {
      meta: {},
      payload: {
        price_point_id: payload.price_point_id,
        full_price: payload.full_price,
        is_gold: payload.is_gold,
        is_sale: payload.is_sale,
      },
    },
  );
}

export function createBundlePurchaseFromConfirmationToken(
  bundleUuid: string,
  payload: BundlePaymentSetupPayload,
) {
  return apiClient.post<ServerSingleResponse<Bundle>>(
    `/v1/bundles/${bundleUuid}/purchase-from-confirmation-token`,
    {
      meta: {},
      payload: {
        price_point_id: payload.price_point_id,
        raw_price: payload.raw_price,
        full_price: payload.full_price,
        is_gold: payload.is_gold,
        is_sale: payload.is_sale,
        confirmation_token_id: payload.confirmationTokenId,
      },
    },
  );
}

export function createBundlePurchaseFromSavedPaymentMethod(
  bundleUuid: string,
  payload: BundleCheckoutSessionPayload,
) {
  return apiClient.post<ServerSingleResponse<Bundle>>(
    `/v1/bundles/${bundleUuid}/purchase-from-saved-payment-method`,
    {
      meta: {},
      payload: {
        price_point_id: payload.price_point_id,
        raw_price: payload.raw_price,
        full_price: payload.full_price,
        is_gold: payload.is_gold,
        is_sale: payload.is_sale,
      },
    },
  );
}

export type ReleasePdfCheckoutSessionPayload = {
  netAmount: number;
};

export type ReleasePdfPaymentSetupPayload = ReleasePdfCheckoutSessionPayload & {
  confirmationTokenId: string;
};

export function createReleasePdfPurchaseFromConfirmationToken(
  releaseId: number,
  payload: ReleasePdfPaymentSetupPayload,
) {
  return apiClient.post<ServerSingleResponse<Release>>(
    `/v1/releases/${releaseId}/pdf-purchase-from-confirmation-token`,
    {
      meta: {},
      payload: {
        netAmount: payload.netAmount,
        confirmation_token_id: payload.confirmationTokenId,
      },
    },
  );
}

export function createReleasePdfPurchaseFromSavedPaymentMethod(
  releaseId: number,
  payload: ReleasePdfCheckoutSessionPayload,
) {
  return apiClient.post<ServerSingleResponse<Release>>(
    `/v1/releases/${releaseId}/pdf-purchase-from-saved-payment-method`,
    {
      meta: {},
      payload: {
        netAmount: payload.netAmount,
      },
    },
  );
}

export type ArtistDonationPaymentPayload = {
  netAmount: number;
  isRecurring: boolean;
  note?: string;
};

export type ArtistDonationPaymentSetupPayload =
  ArtistDonationPaymentPayload & {
    confirmationTokenId: string;
  };

export type ArtistDonationSetupIntentResponse = {
  entity_type: "ArtistDonationSetupIntent";
  provider: string;
  setup_intent_id: string;
  client_secret: string;
};

export type ArtistDonationSetupCompletionResponse = {
  entity_type: "ArtistDonationSetupCompletion";
  provider: string;
  subscription_id: string;
  customer_id: string;
};

export function createArtistDonationFromConfirmationToken(
  artistId: number,
  payload: ArtistDonationPaymentSetupPayload,
) {
  return apiClient.post<ServerSingleResponse<{ status: "success" }>>(
    `/v1/artists/${artistId}/donate-from-confirmation-token`,
    {
      meta: {},
      payload: {
        netAmount: payload.netAmount,
        isRecurring: payload.isRecurring,
        note: payload.note,
        confirmation_token_id: payload.confirmationTokenId,
      },
    },
  );
}

export function createArtistDonationFromSavedPaymentMethod(
  artistId: number,
  payload: ArtistDonationPaymentPayload,
) {
  return apiClient.post<ServerSingleResponse<{ status: "success" }>>(
    `/v1/artists/${artistId}/donate-from-saved-payment-method`,
    {
      meta: {},
      payload: {
        netAmount: payload.netAmount,
        isRecurring: payload.isRecurring,
        note: payload.note,
      },
    },
  );
}

export function createArtistDonationSetupIntent(
  artistId: number,
  payload: ArtistDonationPaymentPayload,
) {
  return apiClient.post<ServerSingleResponse<ArtistDonationSetupIntentResponse>>(
    `/v1/artists/${artistId}/donation-setup-intent`,
    {
      meta: {},
      payload: {
        netAmount: payload.netAmount,
        isRecurring: payload.isRecurring,
        note: payload.note,
      },
    },
  );
}

export function createRecurringArtistDonationFromSetupIntent(
  artistId: number,
  payload: ArtistDonationPaymentPayload & { setupIntentId: string },
) {
  return apiClient.post<
    ServerSingleResponse<ArtistDonationSetupCompletionResponse>
  >(`/v1/artists/${artistId}/recurring-donation-from-setup-intent`, {
    meta: {},
    payload: {
      netAmount: payload.netAmount,
      isRecurring: payload.isRecurring,
      note: payload.note,
      setup_intent_id: payload.setupIntentId,
    },
  });
}

export function createRecurringArtistDonationFromConfirmationToken(
  artistId: number,
  payload: ArtistDonationPaymentPayload & { confirmationTokenId: string },
) {
  return apiClient.post<
    ServerSingleResponse<ArtistDonationSetupCompletionResponse>
  >(`/v1/artists/${artistId}/recurring-donation-from-confirmation-token`, {
    meta: {},
    payload: {
      netAmount: payload.netAmount,
      isRecurring: payload.isRecurring,
      note: payload.note,
      confirmation_token_id: payload.confirmationTokenId,
    },
  });
}

export function createRecurringArtistDonationFromSavedPaymentMethod(
  artistId: number,
  payload: ArtistDonationPaymentPayload,
) {
  return apiClient.post<
    ServerSingleResponse<ArtistDonationSetupCompletionResponse>
  >(`/v1/artists/${artistId}/recurring-donation-from-saved-payment-method`, {
    meta: {},
    payload: {
      netAmount: payload.netAmount,
      isRecurring: payload.isRecurring,
      note: payload.note,
    },
  });
}

export type BundleCheckoutSessionPayload = Pick<
  import("../../../types").BundlePurchasePayload,
  "price_point_id" | "raw_price" | "full_price" | "is_gold" | "is_sale"
>;

export type StripePortalFlow =
  | "manage"
  | "payment_method"
  | "cancel"
  | "reactivate";

export type WebSubscriptionPortalSessionResponse = {
  entity_type: "WebSubscriptionPortalSession";
  provider: string;
  portal_url: string;
};

export function createWebSubscriptionPortalSession(
  flow: StripePortalFlow = "manage",
) {
  return apiClient.post<ServerSingleResponse<WebSubscriptionPortalSessionResponse>>(
    `/v1/account/web-subscription-portal-session`,
    {
      meta: {},
      payload: { flow },
    },
  );
}

export function releaseCheckoutLock(params?: {
  commerceKind?: string;
  entityType?: string;
  entityId?: number;
}) {
  return apiClient.post(`/v1/account/release-checkout-lock`, {
    meta: {},
    payload: {
      commerceKind: params?.commerceKind,
      entityType: params?.entityType,
      entityId: params?.entityId,
    },
  });
}

export type PendingAuthPaymentConfigTypes =
  | WebPurchasePayloadTypes
  | WebSubscriptionCheckoutSessionPayload
  | WebSubscriptionSetupSessionPayload
  | GiftCardPaymentSetupPayload
  | ReleasePaymentSetupPayload
  | BundlePaymentSetupPayload;

export function isWebPurchasePayload(
  payload: unknown,
): payload is WebPurchasePayloadTypes {
  return isRecord(payload) && "totalMinorUnits" in payload;
}

export function isWebSubscriptionCheckoutPayload(
  payload: unknown,
): payload is WebSubscriptionCheckoutSessionPayload {
  return (
    isRecord(payload) &&
    "accessTokenTypeId" in payload &&
    !("checkoutFlow" in payload)
  );
}

export function isGiftCardPaymentSetupPayload(
  payload: unknown,
): payload is GiftCardPaymentSetupPayload {
  return isRecord(payload) && payload.checkoutFlow === "gift_card_payment_setup";
}

export function isSubscriptionSetupCheckoutPayload(
  payload: unknown,
): payload is WebSubscriptionSetupSessionPayload {
  return isRecord(payload) && payload.checkoutFlow === "subscription_setup";
}

export function isReleasePaymentSetupPayload(
  payload: unknown,
): payload is ReleasePaymentSetupPayload {
  return isRecord(payload) && payload.checkoutFlow === "release_payment_setup";
}

export function isBundlePaymentSetupPayload(
  payload: unknown,
): payload is BundlePaymentSetupPayload {
  return isRecord(payload) && payload.checkoutFlow === "bundle_payment_setup";
}

export function isStripeBillingManageableSubscription(
  provider?: SubscriptionProviderMetaType | null,
) {
  return provider?.current_code === "web_stripe_billing";
}

export function isLegacyWebSubscription(
  provider?: SubscriptionProviderMetaType | null,
) {
  return provider?.current_code === "web_legacy";
}

export function isLegacySubscriptionPageFlow(
  provider?: SubscriptionProviderMetaType | null,
) {
  return (
    provider?.purchase_action === "legacy_subscription_page" ||
    provider?.manage_action === "legacy_subscription_page"
  );
}

export function hasManageableWebSubscription(
  provider?: SubscriptionProviderMetaType | null,
) {
  return (
    isStripeBillingManageableSubscription(provider) ||
    isLegacyWebSubscription(provider)
  );
}

export function getLegacyWebSubscriptionsUrl(editUrl: string) {
  const base = editUrl.startsWith("http")
    ? editUrl
    : `${window.location.origin}${editUrl}`;
  return new URL("subscriptions", base).toString();
}

export function redirectToLegacyWebSubscriptions(editUrl: string) {
  window.location.assign(getLegacyWebSubscriptionsUrl(editUrl));
}

export const GET_SAVED_PAYMENT_METHODS_QUERY_KEY = [
  "get-saved-payment-methods",
];
export function getSavedPaymentMethods() {
  return apiClient.get<ServerListResponse<OwnerPaymentServiceTokenTypes>>(
    `/v1/account/payment-methods`,
  );
}

export type ReleasePurchasePayload = {
  price_point_id: number;
  raw_price: number;
  is_gold: boolean;
  stripeToken?: string;
  ownerPaymentServiceTokenId?: string;
  full_price?: number;
  is_sale?: boolean;
};
export function unlockRelease(
  releaseId: number,
  payload: ReleasePurchasePayload,
) {
  return apiClient.post<ServerSingleResponse<any>>(
    `/v1/releases/${releaseId}/unlock`,
    {
      meta: {},
      payload: payload,
    },
  );
}

// POD (Print-on-Demand) purchase types
export interface ShippingAddress {
  first_name: string;
  last_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  zip: string;
  country_id: string;
  phone_number?: string;
}

export interface PodPurchasePayload extends ShippingAddress {
  product_id: number;
  price: number;
  quantity?: number;
  stripe_token?: string;
  owner_payment_service_token_id?: number;
  save_address?: boolean;
  taxes_shipping: string; // JWT proof token from tax-shipping endpoint
}

// Tax-shipping request/response types
export interface TaxShippingRequest {
  product_id: number;
  price: number;
  quantity?: number;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  zip: string;
  country_id: string;
  currency_code?: string;
}

export interface TaxShippingAddress {
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  zip: string;
  country_id: string;
}

export interface TaxShippingResponse {
  product_id: number;
  address: TaxShippingAddress;
  price: number;
  quantity: number;
  currency: string;
  shipping: number;
  taxes: number;
  proof: string; // JWT token
}

export function getTaxAndShipping(payload: TaxShippingRequest) {
  return apiClient.post<ServerSingleResponse<TaxShippingResponse>>(
    `/v1/pod/tax-shipping`,
    { meta: {}, payload },
  );
}

export interface PodPurchaseResponse {
  status: string;
  shipping_address: ShippingAddress;
  estimated_delivery: string;
}

export function purchasePod(payload: PodPurchasePayload) {
  return apiClient.post<ServerSingleResponse<PodPurchaseResponse>>(
    `/v1/pod/purchase`,
    {
      meta: {},
      payload,
    },
  );
}

// Saved address types
export interface SavedAddress {
  id: number;
  address_type_id: number;
  display_name: string;
  firstname: string;
  lastname: string;
  phone_number: string;
  address_line1: string;
  address_line2: string;
  address_line3: string;
  city: string;
  state: string;
  zip: string;
  country_id: string;
}

export const GET_SAVED_ADDRESSES_QUERY_KEY = ["get-saved-addresses"];
export function getSavedAddresses() {
  return apiClient.get<ServerSingleResponse<SavedAddress>>(
    `/v1/account/address`,
  );
}
